import React, { memo, useEffect } from "react";

const NXP_CSS_ID = "__nxp_css";
const NXP_CSS = `
/* ===== Nexus Pagination (nxp-*) ===== */
.nxp-wrap{
  display:flex;
  justify-content:center;
  align-items:center;
  margin-top:var(--nxp-mt,12px);
  flex-wrap:wrap;
  gap:6px;
  font-family:system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;
}
.nxp-btn{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  min-width:36px;
  height:36px;
  padding:0 10px;
  border:1px solid var(--nxp-border, rgba(17,24,39,.15));
  border-radius:8px;
  background:#fff;
  cursor:pointer;
  user-select:none;
  font-size:14px;
  color:var(--nxp-text, #111827);
  transition:all 0.15s ease;
  flex-shrink:0;
  box-sizing:border-box;
}
.nxp-btn:hover:not([disabled]){
  background:var(--nxp-hover-bg, #f3f4f6);
  border-color:var(--nxp-hover-border, rgba(17,24,39,.25));
}
.nxp-btn[disabled]{ cursor:not-allowed; opacity:.65; }
.nxp-btn--active{
  background:var(--nxp-active-bg, #111827);
  color:var(--nxp-active-fg, #fff);
  border-color:var(--nxp-active-bg, #111827);
  cursor:default;
}
.nxp-ellipsis{
  color:var(--nxp-muted, #4b5563);
  flex-shrink:0;
}
.nxp-info{
  margin-left:4px;
  font-size:13px;
  color:var(--nxp-muted, #4b5563);
  white-space:nowrap;
  flex-shrink:0;
}
`;

const Pagination = memo(function Pagination({
  page = 1,
  totalPages = 1,
  loading = false,
  onPageChange = () => {},
}) {
  // Ensure CSS once
  useEffect(() => {
    if (!document.getElementById(NXP_CSS_ID)) {
      const s = document.createElement("style");
      s.id = NXP_CSS_ID;
      s.textContent = NXP_CSS;
      document.head.appendChild(s);
    }
  }, []);

  if (totalPages <= 1) return null;

  const go = (p) => () => {
    if (!loading && p >= 1 && p <= totalPages && p !== page) onPageChange(p);
  };

  const windowSize = 5;
  const half = Math.floor(windowSize / 2);
  let start = Math.max(1, page - half);
  let end = Math.min(totalPages, start + windowSize - 1);
  start = Math.max(1, Math.min(start, end - windowSize + 1));

  const pages = [];
  for (let i = start; i <= end; i++) pages.push(i);

  const btnCls = "nxp-btn";
  const btnActiveCls = "nxp-btn nxp-btn--active";

  return (
    <div className="nxp-wrap">
      <button
        className={btnCls}
        disabled={loading || page <= 1}
        onClick={go(1)}
        aria-label="First page"
      >
        «
      </button>
      <button
        className={btnCls}
        disabled={loading || page <= 1}
        onClick={go(page - 1)}
        aria-label="Previous page"
      >
        ‹
      </button>

      {start > 1 && (
        <button className={btnCls} disabled={loading} onClick={go(1)}>
          1
        </button>
      )}
      {start > 2 && <span className="nxp-ellipsis">…</span>}

      {pages.map((p) => (
        <button
          key={p}
          className={p === page ? btnActiveCls : btnCls}
          disabled={loading || p === page}
          onClick={go(p)}
          aria-current={p === page ? "page" : undefined}
        >
          {p}
        </button>
      ))}

      {end < totalPages - 1 && <span className="nxp-ellipsis">…</span>}
      {end < totalPages && (
        <button className={btnCls} disabled={loading} onClick={go(totalPages)}>
          {totalPages}
        </button>
      )}

      <button
        className={btnCls}
        disabled={loading || page >= totalPages}
        onClick={go(page + 1)}
        aria-label="Next page"
      >
        ›
      </button>
      <button
        className={btnCls}
        disabled={loading || page >= totalPages}
        onClick={go(totalPages)}
        aria-label="Last page"
      >
        »
      </button>
    </div>
  );
});

export default Pagination;
